import { CalendarRange, Clock, PencilLine, Trash2, Users } from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AUDIENCE_LABEL, CATEGORY, CLASS_SCOPE_LABEL } from '../constants';
import type { CalendarEventItem } from '../types/calendar';

export interface EventDetailsModalProps {
  eventItem: CalendarEventItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Both absent on a read-only calendar, which then offers only Close. */
  onEdit?: (item: CalendarEventItem) => void;
  onRemove?: (item: CalendarEventItem) => void;
  /**
   * Whether a holiday on the calendar may be edited or withdrawn here. A
   * holiday carries its own key, separate from the calendar's, so holding the
   * calendar's is not enough.
   */
  canManageHolidays?: boolean;
}

/** One labelled fact about the entry. */
function Detail({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof Clock;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-line bg-surface-2 p-3">
      <span
        className="grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent"
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <p className="lbl text-[11px] uppercase tracking-wider text-ink-3">{label}</p>
        <p className="mt-0.5 text-[13px] font-bold text-ink">{children}</p>
      </div>
    </div>
  );
}

/**
 * Everything held about one entry, before deciding to change it.
 *
 * Opening the editor straight from a click is how a reader who only wanted to
 * know when the exams start ends up in a form with a save button. The details
 * come first; editing and removing are offered from here.
 *
 * A holiday is drawn here too: it is a row in `holidays`, which is what the
 * attendance register reads, so its Edit and Withdraw go to the holiday form
 * and the holidays endpoint (the page decides which) — and only for someone
 * holding the holiday key.
 */
export function EventDetailsModal({
  eventItem,
  isOpen,
  onClose,
  onEdit,
  onRemove,
  canManageHolidays = false,
}: EventDetailsModalProps) {
  if (!eventItem) return null;

  const isHoliday = eventItem.source === 'HOLIDAY';
  const style = CATEGORY[eventItem.category];
  const Icon = style.icon;
  const days = eventItem.days;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={eventItem.title}
      description="What is on the institute calendar for this entry."
      size="md"
      footer={
        (isHoliday && !canManageHolidays) || !onEdit || !onRemove ? (
          <Button type="button" variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button
              type="button"
              variant="dangerSoft"
              onClick={() => onRemove(eventItem)}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              {isHoliday ? 'Withdraw' : 'Remove'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              type="button"
              onClick={() => onEdit(eventItem)}
              leftIcon={<PencilLine className="h-4 w-4" />}
            >
              {isHoliday ? 'Edit holiday' : 'Edit event'}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-4">
        <span
          className={cn(
            'inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-wider',
            style.chip,
          )}
        >
          <Icon className="h-3.5 w-3.5" aria-hidden />
          {style.label}
        </span>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Detail icon={CalendarRange} label="When">
            {formatDate(eventItem.startDate)}
            {eventItem.endDate !== eventItem.startDate && ` → ${formatDate(eventItem.endDate)}`}
            {days > 1 && ` (${days} days)`}
          </Detail>
          <Detail icon={Clock} label="Timing">
            {formatTimeRange(eventItem.startTime, eventItem.endTime)}
          </Detail>
          <Detail icon={Users} label="Classes">
            {CLASS_SCOPE_LABEL[eventItem.classScope]}
          </Detail>
          <Detail icon={Users} label="Who it is for">
            {AUDIENCE_LABEL[eventItem.audience]}
          </Detail>
        </div>

        <div className="rounded-2xl border border-line bg-surface-2 p-3">
          <p className="lbl text-[11px] uppercase tracking-wider text-ink-3">Details</p>
          <p className="mt-1 text-[13px] font-medium text-ink-2">
            {eventItem.description || 'No further details were added for this entry.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
